/**
 * Shared loading and error state components
 *
 * Used by CaseViewer and LabelingPage for consistent UI
 */

import React from "react";

interface FullScreenLoadingProps {
  message: string;
}

export const FullScreenLoading: React.FC<FullScreenLoadingProps> = ({ message }) => (
  <div className="flex h-screen items-center justify-center bg-bg-2 text-fg-muted">
    <div className="text-center">
      <div className="mb-4 text-sm">{message}</div>
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-border-strong border-t-fg-dim mx-auto"></div>
    </div>
  </div>
);

interface FullScreenErrorProps {
  title: string;
  message?: string;
}

export const FullScreenError: React.FC<FullScreenErrorProps> = ({ title, message }) => (
  <div className="flex h-screen items-center justify-center bg-bg-2 text-fg-muted">
    <div className="text-center">
      <div className="text-warn mb-4">{title}</div>
      {message && <div className="text-sm text-fg-muted">{message}</div>}
    </div>
  </div>
);

interface FullScreenEmptyProps {
  message: string;
}

export const FullScreenEmpty: React.FC<FullScreenEmptyProps> = ({ message }) => (
  <div className="flex h-screen items-center justify-center bg-bg-2 text-fg-muted">
    <div className="text-center">
      <div className="mb-4 text-sm">{message}</div>
    </div>
  </div>
);

interface ViewerLoadingProps {
  title: string;
  subtitle?: string;
  progress?: {
    current?: number;
    total?: number;
    stage?: string;
  };
}

export const ViewerLoading: React.FC<ViewerLoadingProps> = ({ title, subtitle, progress }) => (
  <div className="flex items-center justify-center h-full text-fg-muted">
    <div className="text-center max-w-md px-4">
      <div className="text-lg font-semibold mb-2">{title}</div>

      {/* Progress bar when we have numerical progress */}
      {progress?.current !== undefined && progress?.total !== undefined && progress.total > 0 ? (
        <div className="mb-4">
          <div className="w-48 mx-auto bg-surface-2 rounded-full h-2 overflow-hidden">
            <div
              className="bg-signal h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${Math.min(100, (progress.current / progress.total) * 100)}%` }}
            />
          </div>
          <div className="text-xs text-fg-muted mt-1">
            {progress.current} / {progress.total}
          </div>
        </div>
      ) : (
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
      )}

      {subtitle && <div className="text-xs text-fg-muted">{subtitle}</div>}
      {progress?.stage && !subtitle && (
        <div className="text-xs text-fg-muted">{progress.stage}</div>
      )}
    </div>
  </div>
);

interface ViewerErrorProps {
  title: string;
  message?: string;
  actions?: React.ReactNode;
}

export const ViewerError: React.FC<ViewerErrorProps> = ({ title, message, actions }) => (
  <div className="flex items-center justify-center h-full text-fg-muted">
    <div className="text-center max-w-md px-4">
      <div className="text-warn mb-4 text-lg font-semibold">{title}</div>
      {message && <div className="text-sm text-fg-muted mb-4">{message}</div>}
      {actions && <div className="flex gap-2 justify-center">{actions}</div>}
    </div>
  </div>
);

interface NavigationButtonsProps {
  onPrevious: () => void;
  onNext: () => void;
  onNextUnlabeled?: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  hasNextUnlabeled?: boolean;
}

export const NavigationButtons: React.FC<NavigationButtonsProps> = ({
  onPrevious,
  onNext,
  onNextUnlabeled,
  hasPrevious,
  hasNext,
  hasNextUnlabeled,
}) => (
  <div className="flex items-center gap-2">
    <button
      onClick={onPrevious}
      disabled={!hasPrevious}
      className="px-3 py-2 bg-surface text-white rounded font-medium disabled:opacity-50 hover:bg-surface-2 transition-colors flex items-center gap-1.5"
      title="Previous case (←)"
    >
      <span>← Prev</span>
      <kbd className="text-[10px] opacity-60 bg-surface-2 px-1.5 py-0.5 rounded">←</kbd>
    </button>
    <button
      onClick={onNext}
      disabled={!hasNext}
      className="px-3 py-2 bg-surface text-white rounded font-medium disabled:opacity-50 hover:bg-surface-2 transition-colors flex items-center gap-1.5"
      title="Next case (→)"
    >
      <span>Next</span>
      <kbd className="text-[10px] opacity-60 bg-surface-2 px-1.5 py-0.5 rounded">→</kbd>
    </button>
    {onNextUnlabeled && (
      <button
        onClick={onNextUnlabeled}
        disabled={!hasNextUnlabeled}
        className="px-3 py-2 bg-signal text-white rounded font-medium disabled:opacity-50 hover:bg-signal transition-colors flex items-center gap-1.5"
        title="Skip to next unlabeled case (N)"
      >
        <span>Next Unlabeled</span>
        <kbd className="text-[10px] opacity-60 bg-signal-tint px-1.5 py-0.5 rounded">N</kbd>
      </button>
    )}
  </div>
);
